// src/pages/Accounting/Reports/AccountList/utils.ts
// Account List report utilities: currency formatting, filename building,
// relative time strings, DOM scoping for print and export.

import type { AccountListRow, ReportCustomization } from './types';

export function fmtCurrency(
  value: number | null | undefined,
  opts: { hideSymbol?: boolean; divideByThousand?: boolean; negativeStyle?: 'parentheses' | 'minus' } = {}
): string {
  if (value == null) return '';
  const raw = opts.divideByThousand ? value / 1000 : value;
  const abs = Math.abs(raw);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const symbol = opts.hideSymbol ? '' : '$';
  if (raw < 0) {
    return opts.negativeStyle === 'minus' ? '-' + symbol + formatted : '(' + symbol + formatted + ')';
  }
  return symbol + formatted;
}

export function fmtRelativeTime(from: Date | null): string {
  if (!from) return 'never refreshed';
  const diffMs = Date.now() - from.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 30) return 'Refreshed just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 1) return 'Refreshed less than a minute ago';
  if (diffMin === 1) return 'Refreshed 1 minute ago';
  if (diffMin < 60) return 'Refreshed ' + diffMin + ' minutes ago';
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr === 1) return 'Refreshed 1 hour ago';
  if (diffHr < 24) return 'Refreshed ' + diffHr + ' hours ago';
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Refreshed 1 day ago';
  return 'Refreshed ' + diffDay + ' days ago';
}

export function fmtGeneratedOn(date: Date): string {
  const dateStr = date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return 'Generated on: ' + dateStr + ' · ' + timeStr;
}

export function applyCustomization(rows: AccountListRow[], c: ReportCustomization): AccountListRow[] {
  if (!c.filters.length) return rows;
  return rows.filter(function (row) {
    return c.filters.every(function (f) {
      const raw = (row as unknown as Record<string, unknown>)[f.field];
      const str = raw == null ? '' : String(raw).toLowerCase();
      const val = (f.value ?? '').toLowerCase();
      if (f.condition === 'equals') return str === val;
      if (f.condition === 'notEquals') return str !== val;
      if (f.condition === 'contains') return str.includes(val);
      if (f.condition === 'startsWith') return str.startsWith(val);
      return true;
    });
  });
}

export function visibleColumns(c: ReportCustomization) {
  return [...c.columns].filter(function (col) { return col.visible; }).sort(function (a, b) { return a.order - b.order; });
}

export function buildEmailDefaults(reportName: string, companyName: string, userName: string | null) {
  return {
    to: '',
    cc: '',
    subject: reportName,
    format: 'xlsx' as const,
    message:
      'Hello,\n\n' +
      "Attached is the '" + reportName + "' report for " + companyName + '.\n\n' +
      'Regards,\n' +
      (userName || ''),
    fileName: reportName,
  };
}